import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { UserPlus, Mail, CreditCard, Music } from 'lucide-react';
import GeometricBackground from '@/components/GeometricBackground';
import { Button } from '@/components/ui/button';
import LegalLinks from '@/components/legal/LegalLinks';
import { LEGAL_ENTITY } from '@/constants/legalEntity';

const HowItWorksPage: React.FC = () => {
  const steps = [
    {
      icon: UserPlus,
      title: 'Teacher studio is set up',
      body: `Music teachers and schools do not sign up on the public website. ${LEGAL_ENTITY.legalNameEn} creates your studio account after onboarding. You receive secure access to the teacher dashboard.`,
    },
    {
      icon: Mail,
      title: 'Invite your students',
      body: 'From the dashboard you invite students by email or shareable link. Each student gets their own login to book lessons, view materials, and pay for packages you define.',
    },
    {
      icon: CreditCard,
      title: 'Students subscribe or buy AI practice',
      body: 'Students pay for lesson subscriptions (recurring) or one-time AI practice features at prices you set. Checkout is handled securely via Lemon Squeezy.',
    },
    {
      icon: Music,
      title: 'Teach and grow',
      body: 'Manage bookings, packages, payouts overview, blog, and your public portfolio site — all in one place.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>How it works — {LEGAL_ENTITY.productName}</title>
        <meta
          name="description"
          content="Arrangelly is invite-only: we onboard teachers, teachers invite students. No public signup."
        />
      </Helmet>

      <div className="relative min-h-screen overflow-hidden">
        <GeometricBackground />
        <div className="relative z-10 py-20">
          <motion.div
            className="container mx-auto px-4 max-w-3xl text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">How it works</h1>
            <p className="text-lg text-slate-600">
              {LEGAL_ENTITY.productName} is built for professional music teachers. Access is{' '}
              <strong>by invitation</strong> — there is no open registration on this website.
            </p>
          </motion.div>

          <div className="container mx-auto px-4 max-w-2xl space-y-8 mb-14">
            {steps.map((step, i) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.08 }}
                className="flex gap-5 p-6 rounded-2xl bg-white/85 border border-slate-200/90 shadow-sm"
              >
                <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center shrink-0">
                  <step.icon className="text-violet-700" size={24} />
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-bold text-slate-900 mb-1">{step.title}</h2>
                  <p className="text-slate-600 text-sm leading-relaxed">{step.body}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="container mx-auto px-4 text-center space-y-6">
            <p className="text-slate-600">
              Interested in {LEGAL_ENTITY.productName} for your studio?{' '}
              <Link to="/contact" className="text-violet-700 font-medium hover:underline">
                Contact us
              </Link>
              .
            </p>
            <Button asChild className="rounded-full">
              <Link to="/pricing">View pricing</Link>
            </Button>
            <div className="pt-8">
              <LegalLinks className="justify-center text-sm" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HowItWorksPage;
